import '../../styles/components/admin.css'
import { requireAuth, getBlogPost, createBlogPost, updateBlogPost, uploadProductImage } from '../../lib/supabase.js'

export function renderBlogForm(outlet, params) {
  requireAuth(async () => {
    const isEdit = !!params?.id
    let post = null

    if (isEdit) {
      try {
        post = await getBlogPost(params.id)
      } catch (err) {
        outlet.innerHTML = `<div class="admin-page"><div class="container"><p class="text-secondary">Post not found.</p></div></div>`
        return
      }
    }

    outlet.innerHTML = `
      <div class="admin-page">
        <div class="container" style="max-width: 800px">
          <div class="admin-topbar">
            <h1 class="admin-title">${isEdit ? 'Edit Post' : 'New Post'}</h1>
            <a href="#/admin/blog" class="btn btn-sm">← Back to Posts</a>
          </div>

          <form id="blog-form" style="display:flex; flex-direction:column; gap: var(--space-5);">
            <div class="form-group">
              <label class="form-label" for="post-title">Title *</label>
              <input class="form-input" type="text" id="post-title" name="title"
                placeholder="Post title" value="${escapeAttr(post?.title || '')}" required>
            </div>

            <div class="form-group">
              <label class="form-label" for="post-slug">Slug *</label>
              <input class="form-input" type="text" id="post-slug" name="slug"
                placeholder="post-url-slug" value="${escapeAttr(post?.slug || '')}">
              <span style="font-size: var(--text-xs); color: var(--color-text-muted)">Auto-generated from title if left empty</span>
            </div>

            <div class="form-group">
              <label class="form-label" for="post-excerpt">Excerpt</label>
              <textarea class="form-textarea" id="post-excerpt" name="excerpt" rows="2"
                placeholder="Brief summary for previews and meta descriptions">${escapeHtml(post?.excerpt || '')}</textarea>
            </div>

            <div class="form-group">
              <label class="form-label" for="post-body">Body * (Markdown supported)</label>
              <textarea class="form-textarea" id="post-body" name="body" rows="20"
                placeholder="Write your post content here..." style="font-family: var(--font-mono); font-size: var(--text-sm);">${escapeHtml(post?.body || '')}</textarea>
            </div>

            <div class="form-group">
              <label class="form-label">Cover Image</label>
              <div style="display:flex; gap: var(--space-3); align-items:center;">
                <input type="file" id="cover-upload" accept="image/*" style="font-size: var(--text-sm);">
                <span id="cover-status" style="font-size: var(--text-xs); color: var(--color-text-muted);">
                  ${post?.cover_image ? 'Current image set' : 'No image'}
                </span>
              </div>
              <input type="hidden" id="cover-url" value="${escapeAttr(post?.cover_image || '')}">
              ${post?.cover_image ? `<img src="${escapeAttr(post.cover_image)}" style="max-width:200px; margin-top: var(--space-2); border-radius: var(--radius);">` : ''}
            </div>

            <div style="display:grid; grid-template-columns: 1fr 1fr; gap: var(--space-4);">
              <div class="form-group">
                <label class="form-label" for="post-meta-title">SEO Title</label>
                <input class="form-input" type="text" id="post-meta-title" name="meta_title"
                  placeholder="Override page title" value="${escapeAttr(post?.meta_title || '')}">
              </div>
              <div class="form-group">
                <label class="form-label" for="post-tags">Tags (comma-separated)</label>
                <input class="form-input" type="text" id="post-tags" name="tags"
                  placeholder="knife sharpening, belt grinder" value="${escapeAttr(post?.tags?.join(', ') || '')}">
              </div>
            </div>

            <div class="form-group">
              <label class="form-label" for="post-meta-desc">SEO Description</label>
              <textarea class="form-textarea" id="post-meta-desc" name="meta_description" rows="2"
                placeholder="Override meta description">${escapeHtml(post?.meta_description || '')}</textarea>
            </div>

            <div style="display:grid; grid-template-columns: 1fr 1fr; gap: var(--space-4);">
              <div class="form-group">
                <label class="form-label" for="post-status">Status</label>
                <select class="form-input" id="post-status" name="status">
                  <option value="draft" ${post?.status === 'draft' ? 'selected' : ''}>Draft</option>
                  <option value="scheduled" ${post?.status === 'scheduled' ? 'selected' : ''}>Scheduled</option>
                  <option value="published" ${post?.status === 'published' ? 'selected' : ''}>Published</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label" for="post-publish-at">Publish Date</label>
                <input class="form-input" type="datetime-local" id="post-publish-at" name="publish_at"
                  value="${post?.publish_at ? toLocalDatetime(post.publish_at) : ''}">
              </div>
            </div>

            <div style="display:flex; gap: var(--space-3); padding-top: var(--space-4);">
              <button type="submit" class="btn btn-primary" id="save-btn">
                ${isEdit ? 'Update Post' : 'Create Post'}
              </button>
              <button type="button" class="btn btn-primary" id="publish-now-btn">
                Publish Now
              </button>
              <a href="#/admin/blog" class="btn btn-secondary">Cancel</a>
            </div>

            <div id="form-error" style="display:none; color: var(--color-error); font-size: var(--text-sm);"></div>
          </form>
        </div>
      </div>
    `

    // Auto-generate slug from title
    const titleInput = document.getElementById('post-title')
    const slugInput = document.getElementById('post-slug')
    if (!isEdit) {
      titleInput.addEventListener('input', () => {
        if (!slugInput.dataset.manual) {
          slugInput.value = slugify(titleInput.value)
        }
      })
    }
    slugInput.addEventListener('input', () => { slugInput.dataset.manual = '1' })

    // Cover image upload
    document.getElementById('cover-upload')?.addEventListener('change', async (e) => {
      const file = e.target.files?.[0]
      if (!file) return
      const status = document.getElementById('cover-status')
      status.textContent = 'Uploading…'
      try {
        const url = await uploadProductImage(file)
        document.getElementById('cover-url').value = url
        status.textContent = 'Uploaded'
      } catch (err) {
        status.textContent = 'Upload failed: ' + err.message
      }
    })

    // Publish Now button
    document.getElementById('publish-now-btn')?.addEventListener('click', () => {
      document.getElementById('post-status').value = 'published'
      document.getElementById('blog-form').requestSubmit()
    })

    // Form submit
    document.getElementById('blog-form')?.addEventListener('submit', async (e) => {
      e.preventDefault()
      const errorEl = document.getElementById('form-error')
      const saveBtn = document.getElementById('save-btn')
      errorEl.style.display = 'none'

      const title = titleInput.value.trim()
      const slug = slugInput.value.trim() || slugify(title)
      const body = document.getElementById('post-body').value.trim()

      if (!title || !body) {
        errorEl.textContent = 'Title and body are required.'
        errorEl.style.display = 'block'
        return
      }

      const status = document.getElementById('post-status').value
      const publishAt = document.getElementById('post-publish-at').value || null
      const tagsRaw = document.getElementById('post-tags').value
      const tags = tagsRaw ? tagsRaw.split(',').map(t => t.trim()).filter(Boolean) : []

      const postData = {
        title,
        slug,
        excerpt: document.getElementById('post-excerpt').value.trim() || null,
        body,
        cover_image: document.getElementById('cover-url').value || null,
        status,
        publish_at: publishAt ? new Date(publishAt).toISOString() : null,
        published_at: status === 'published' ? (post?.published_at || new Date().toISOString()) : null,
        meta_title: document.getElementById('post-meta-title').value.trim() || null,
        meta_description: document.getElementById('post-meta-desc').value.trim() || null,
        tags,
      }

      saveBtn.disabled = true
      saveBtn.textContent = 'Saving…'

      try {
        if (isEdit) {
          await updateBlogPost(params.id, postData)
        } else {
          await createBlogPost(postData)
        }
        window.location.hash = '#/admin/blog'
      } catch (err) {
        errorEl.textContent = err.message || 'Failed to save post.'
        errorEl.style.display = 'block'
        saveBtn.disabled = false
        saveBtn.textContent = isEdit ? 'Update Post' : 'Create Post'
      }
    })
  })
}

function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

function toLocalDatetime(iso) {
  const d = new Date(iso)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function escapeAttr(str) {
  return String(str || '').replace(/"/g, '&quot;').replace(/&/g, '&amp;')
}
